<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';

// --- Component Properties ---
const props = defineProps<{
  source: string;
  // used to build a unique DOM id for the rendered svg
  diagramId?: number | string | null;
}>();

// --- Emits ---
const emits = defineEmits<{
  (e: 'rendered'): void;
}>();

const svg = ref('');
const error = ref<string | null>(null);
let seq = 0;

async function render() {
  const source = props.source?.trim();
  if (!source) {
    svg.value = '';
    error.value = null;
    return;
  }
  try {
    // dynamic import keeps mermaid out of the main bundle
    const { default: mermaid } = await import('mermaid');
    mermaid.initialize({ startOnLoad: false, securityLevel: 'strict' });
    // mermaid renders into a detached node, so a collapsed parent is fine
    const { svg: rendered } = await mermaid.render(
      `mmd-${props.diagramId ?? 'x'}-${seq++}`,
      source
    );
    svg.value = rendered;
    error.value = null;
    emits('rendered');
  } catch (e) {
    console.error('Failed to render mermaid diagram', e);
    error.value = e instanceof Error ? e.message : String(e);
    svg.value = '';
  }
}

onMounted(render);
watch(() => props.source, render);
</script>

<template>
  <div v-if="error" class="text-danger small table-border mb-1">
    Failed to render diagram: {{ error }}
  </div>
  <!-- mermaid sanitizes its own output under securityLevel: 'strict' -->
  <div v-else class="mermaid-diagram mb-1" v-html="svg" />
</template>

<style scoped>
/* scoped styles do not reach v-html content without :deep() */
.mermaid-diagram :deep(svg) {
  max-width: 100%;
  height: auto;
}
</style>
